<template>
  <div class="bottom-bar">
    <div class="comment-input" @click="handleInputClick">
      {{ t("postDetail.writeSomething") }}
    </div>
    <div class="actions">
      <div class="action" @click="handleCommentClick">
        <img src="@/assets/comment.png" alt="comment" class="icon" />
        <span class="action-text">{{
          post?.comments || post?.replies || 0
        }}</span>
      </div>
      <div class="action" @click="handleStar" v-if="!isReplyDetail">
        <div
          class="icon star-icon"
          :class="{ 'is-active': post?.isStarred }"
        ></div>
        <span class="action-text">{{ post?.stars || 0 }}</span>
      </div>
      <div class="action" @click="handleLike">
        <div
          class="icon like-icon"
          :class="{ 'is-active': post?.isLiked || post?.is_liked }"
        ></div>
        <span class="action-text">{{ post?.likes || 0 }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import {
  useUserStore,
  useFavoriteStore,
  useLikeStore,
  useToastStore,
} from "@/stores";
import { clickButton, pageType } from "@/utils/point";
const { t } = useI18n();

interface Props {
  post?: any; // 只需要 post 属性，用于获取所有数据和更新父组件
  isReplyDetail?: boolean; // 是否为回复详情页面
}
interface Emits {
  (e: "comment-click"): void;
  (e: "update:post", value: any): void;
}

const props = withDefaults(defineProps<Props>(), {
  post: null,
  isReplyDetail: false,
});

const emit = defineEmits<Emits>();
const userStore = useUserStore();
const favoriteStore = useFavoriteStore();
const likeStore = useLikeStore();
const toast = useToastStore();

const handleCommentClick = () => {
  if (!userStore.checkBind()) {
    return;
  }
  emit("comment-click");
};

const handleInputClick = () => {
  if (!userStore.checkBind()) {
    return;
  }
  emit("comment-click");
};

const handleStar = async () => {
  if (!userStore.checkBind()) {
    return;
  }

  // 获取帖子ID
  const tid = props.post?.tid;
  if (!tid) return;

  try {
    if (props.post?.isStarred) {
      // 取消收藏
      await favoriteStore.cancelFavorite(tid);
      clickButton({
        eventType: pageType.POST_DETAIL,
        eventReason: "取消收藏",
        eventValue: JSON.stringify({
          tid: tid,
        }),
      });
      toast.showToast(t("postDetail.cancelFavorite"), "success");
    } else {
      // 添加收藏
      await favoriteStore.addFavorite(tid);
      clickButton({
        eventType: pageType.POST_DETAIL,
        eventReason: "收藏",
        eventValue: JSON.stringify({
          tid: tid,
        }),
      });
      toast.showToast(t("postDetail.favoriteSuccess"), "success");
    }

    // 更新父组件数据
    if (props.post) {
      const updatedPost = {
        ...props.post,
        isStarred: !props.post.isStarred,
        stars: props.post.isStarred
          ? Math.max(0, props.post.stars - 1)
          : props.post.stars + 1,
      };
      emit("update:post", updatedPost);
    }
  } catch (error) {
    console.error("收藏操作失败:", error);
    toast.showToast(t("postDetail.operationFailed"), "error");
  }
};

const handleLike = async () => {
  if (!userStore.checkBind()) {
    return;
  }
  // 获取ID，如果是回复详情页面，使用评论ID
  const id = props.isReplyDetail ? props.post?.pid : props.post?.tid;
  if (!id) return;
  try {
    if (props.post?.isLiked) {
      // 取消点赞
      await likeStore.cancelLike(id, props.isReplyDetail ? "post" : "thread");
      toast.showToast(t("profile.cancelLikeSuccess"), "success");
    } else {
      // 添加点赞
      await likeStore.addLike(id, props.isReplyDetail ? "post" : "thread");
      toast.showToast(t("profile.addLikeSuccess"), "success");
    }

    // 更新父组件数据
    if (props.post) {
      const updatedPost = {
        ...props.post,
        isLiked: !props.post.isLiked,
        likes: props.post.isLiked
          ? Math.max(0, props.post.likes - 1)
          : props.post.likes + 1,
      };
      emit("update:post", updatedPost);
    }
  } catch (error) {
    console.error("点赞操作失败:", error);
    toast.showToast(t("postDetail.operationFailed"), "error");
  }
};
</script>

<style scoped lang="scss">
$primary-color: #dd8632;

.bottom-bar {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  right: 0;
  bottom: 0;
  max-width: 800px;
  width: 100%;
  background: #fff;
  border-top: 1px solid #f7f7f7;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  z-index: 20;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);

  .comment-input {
    flex: 1;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #eee;
    padding: 0 16px;
    font-size: 12px;
    background: #f7f7f7;
    margin-right: 10px;
    display: flex;
    align-items: center;
    color: #999;
    cursor: pointer;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 24px;

    .action {
      display: flex;
      flex-direction: column;
      align-items: center;
      color: #717171;
      cursor: pointer;
    }
  }
}

.icon {
  width: 20px;
  height: 20px;
  margin-bottom: 1px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  transition: all 0.3s ease;

  &.star-icon {
    background-image: url("@/assets/star.png");
  }

  &.star-icon.is-active {
    background-image: url("@/assets/star-active.png");
  }

  &.like-icon {
    background-image: url("@/assets/like.png");
  }

  &.like-icon.is-active {
    background-image: url("@/assets/like-active.png");
  }
}

.action-text {
  font-size: 12px;
}
</style>
